import React from 'react';
import { Outlet, useNavigate, Link as RouterLink } from 'react-router-dom';
import { Box, Button, Container, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated } from '../store/slices/authSlice';
import BrandLogo from './brand/BrandLogo';
import LanguageSwitcher from './brand/LanguageSwitcher';
import ContactInquiryButton from './brand/ContactInquiryButton';
import { RichText, useI18n } from '../i18n';
import { colors, shadows } from '../design/tokens';
import useEmbedMode from '../hooks/useEmbedMode';

export default function MarketingLayout() {
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { t } = useI18n();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const embed = useEmbedMode();

  return (
    <Box sx={{ minHeight: embed ? 'auto' : '100vh', display: 'flex', flexDirection: 'column', bgcolor: colors.neutral.surface }}>
      {!embed && (
        <Box
          component="div"
          role="status"
          sx={{
            py: 0.75,
            px: 2,
            textAlign: 'center',
            fontSize: '0.72rem',
            fontWeight: 700,
            letterSpacing: '0.04em',
            bgcolor: colors.semantic?.info ?? 'info.light',
            borderBottom: 1,
            borderColor: 'divider',
            color: 'text.secondary',
          }}
        >
          <RichText text={t('home.honestyStrip')} component="span" />
        </Box>
      )}
      {!embed && (
        <Box
          component="header"
          sx={{
            py: 1.5,
            px: 2,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            position: 'sticky',
            top: 0,
            zIndex: theme.zIndex.appBar,
            boxShadow: shadows.card,
          }}
        >
        <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
          <BrandLogo onClick={() => navigate('/')} />
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
            <LanguageSwitcher />
            {!isMobile && (
              <Button variant="text" size="medium" onClick={() => navigate('/guide')} sx={{ fontWeight: 600 }}>
                {t('nav.guide')}
              </Button>
            )}
            {!isMobile && (
              <ContactInquiryButton variant="text" size="small" showIcon={false} labelKey="contact.ctaShort" />
            )}
            {isAuthenticated ? (
              <Button variant="contained" size="medium" onClick={() => navigate('/transfer')}>
                {t('nav.goTransfer')}
              </Button>
            ) : (
              <Button variant="contained" size="medium" onClick={() => navigate('/auth')}>
                {isMobile ? t('nav.login') : t('nav.loginStart')}
              </Button>
            )}
          </Stack>
        </Container>
      </Box>
      )}

      <Box component="main" sx={{ flex: embed ? 0 : 1 }}>
        <Outlet />
      </Box>

      {!embed && (
      <Box component="footer" sx={{ py: 3, px: 2, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
        <Container maxWidth="md">
          <Stack spacing={2} alignItems="center">
            <Typography variant="body2" color="text.secondary" align="center" component="div">
              <RichText text={t('footer.marketing')} component="span" />
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
              <Button component={RouterLink} to="/guide" variant="text" size="small">
                {t('footer.guideLink')}
              </Button>
              <Button component={RouterLink} to="/guide#guide-link-delivery" variant="text" size="small">
                {t('footer.deliveryLink')}
              </Button>
              <Button
                component="a"
                href="https://bwl-monorepo-minimum.vercel.app/projects"
                target="_blank"
                rel="noopener noreferrer"
                variant="text"
                size="small"
              >
                Min Labs
              </Button>
              <ContactInquiryButton variant="outlined" size="small" />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {t('footer.marketingCopyright', { year: new Date().getFullYear() })}
            </Typography>
          </Stack>
        </Container>
      </Box>
      )}
    </Box>
  );
}
